import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect } from "react";
import { getCatalogRepository } from "@/lib/catalog/repository";
import { CatalogGrid } from "@/components/catalog/CatalogGrid";
import { ProductGridSkeleton } from "@/components/catalog/CatalogStates";
import { track } from "@/lib/customerio/track";
import { CIO_EVENTS } from "@/lib/customerio/events";
import { SafeImage } from "@/components/common/SafeImage";
import { fallbackCollectionCover } from "@/lib/catalog/fallback-images";
import { absoluteImage, canonicalUrl } from "@/lib/seo/site";
import { breadcrumbSchema, clampDescription, itemListSchema, ldScript } from "@/lib/seo/schema";


export const Route = createFileRoute("/collections/$slug")({
  loader: async ({ params }) => {
    const repo = getCatalogRepository();
    const collections = await repo.getCollections();
    const collection = collections.find((c) => c.id === params.slug);
    if (!collection) throw notFound();
    const products = await repo.getProductsByCollection(params.slug);
    return { collection, products };
  },
  pendingComponent: () => <ProductGridSkeleton />,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="text-3xl text-black" style={{ fontFamily: "'Playfair Display', serif" }}>Collection not found</h1>
      <Link to="/collections" className="mt-8 inline-block border border-black px-6 py-3 text-[11px] uppercase tracking-[0.28em] hover:border-[#C6A15B] hover:text-[#C6A15B]">All collections</Link>
    </div>
  ),
  head: ({ loaderData, params }) => {
    const c = loaderData?.collection;
    const products = loaderData?.products ?? [];
    const title = c ? `${c.name} Collection | CB16 Fragrances` : "Collection | CB16 Fragrances";
    const desc = clampDescription(
      c?.description || c?.tagline || "A CB16 fragrance collection.",
    );
    const url = canonicalUrl(`/collections/${params.slug}`);
    const cover = absoluteImage(c?.cover);
    return {
      meta: [
        { title }, { name: "description", content: desc },
        { property: "og:url", content: url },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(cover
          ? [
              { property: "og:image", content: cover },
              { name: "twitter:image", content: cover },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        ldScript(
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Collections", path: "/collections" },
            ...(c ? [{ name: c.name, path: `/collections/${params.slug}` }] : []),
          ]),
        ),
        ...(products.length ? [ldScript(itemListSchema(products))] : []),
      ],
    };
  },

  component: CollectionDetailPage,
});

function CollectionDetailPage() {
  const { collection, products } = Route.useLoaderData();
  useEffect(() => {
    track(CIO_EVENTS.COLLECTION_VIEWED, {
      collection_id: collection.id,
      collection_name: collection.name,
      products: products.length,
    });
  }, [collection.id, collection.name, products.length]);
  return (
    <div className="bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <section className="relative overflow-hidden border-b border-black/10 bg-[#faf7f2]">
        {(
          <div className="absolute inset-0">
            <SafeImage
              src={collection.cover}
              fallback={fallbackCollectionCover(collection.id)}
              alt=""
              className="h-full w-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-white/70 to-white" />
          </div>
        )}
        <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
          <span className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">Collection</span>
          <h1 className="mt-4 text-4xl font-normal leading-tight text-black sm:text-5xl lg:text-6xl" style={{ fontFamily: "'Playfair Display', serif" }}>{collection.name}</h1>
          <div className="mt-6 h-px w-10 bg-[#C6A15B]" />
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-black/70 sm:text-base">{collection.description}</p>
          <p className="mt-4 text-[11px] uppercase tracking-[0.28em] text-black/50">{products.length} {products.length === 1 ? "product" : "products"}</p>
        </div>
      </section>
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10 lg:py-16">
        {products.length === 0 ? (
          <p className="text-sm text-black/60">No products available in this collection. <Link to="/shop" className="underline hover:text-[#C6A15B]">Browse the shop</Link>.</p>
        ) : (
          <CatalogGrid products={products} />
        )}
      </div>
    </div>
  );
}